import { useEffect, useState } from 'react';
import { useBoiteEntreprise } from '../lib/boite-entreprise';
import { liensPublicsApi } from '../api/resources';
import { C } from '../theme/palette';

/**
 * Lien de consultation d'une pièce, demandé au serveur.
 *
 * <p>Le lien se composait à l'écran, du numéro de la pièce et de celui du
 * client, sur un domaine qui n'était pas celui de l'installation : il ne menait
 * nulle part, et se devinait. C'est le serveur qui le délivre, avec un jeton
 * tiré au hasard. Une pièce pas encore enregistrée n'en a pas.
 *
 * @param {'devis'|'facture'|'livraison'} type type de la pièce
 * @param {number|string} id identifiant de la pièce
 * @param {number} [version] à changer pour redemander le lien, après un retrait
 * @param {number} [essais] nombre de demandes avant d'abandonner : une facture
 *        qu'on vient d'émettre n'est pas encore enregistrée au serveur (la
 *        sauvegarde part un instant après), et sa première demande est refusée
 * @returns {{lien: object|null, pret: boolean, echec: boolean}} le lien et ce
 *          qu'on sait de ses consultations ; `pret` devient vrai quand la demande
 *          a abouti ou échoué, `echec` quand aucune demande n'a abouti
 */
export function useLienClient(type, id, version = 0, essais = 1) {
  const [etat, setEtat] = useState({ lien: null, pret: false, echec: false });
  useEffect(() => {
    if (typeof id !== 'number') {
      setEtat({ lien: null, pret: true, echec: false });
      return undefined;
    }
    let annule = false;
    let minuteur;
    setEtat({ lien: null, pret: false, echec: false });
    const demande = (essai) =>
      liensPublicsApi
        .pour(type, id)
        .then((lien) => {
          if (!annule) setEtat({ lien, pret: true, echec: false });
        })
        .catch(() => {
          if (annule) return;
          if (essai < essais) {
            minuteur = setTimeout(() => demande(essai + 1), 1000 * essai);
          } else {
            setEtat({ lien: null, pret: true, echec: true });
          }
        });
    demande(1);
    return () => {
      annule = true;
      clearTimeout(minuteur);
    };
  }, [type, id, version, essais]);
  return etat;
}

const bouton = (fond) => ({
  background: fond,
  color: '#fff',
  borderRadius: 8,
  padding: '8px',
  textAlign: 'center',
  fontSize: 9.5,
  fontWeight: 700,
});

/**
 * Relance d'un client par courriel ou par WhatsApp, avec le lien vers sa pièce.
 *
 * @param {object} props propriétés du composant
 * @param {'devis'|'facture'} props.type type de la pièce
 * @param {number|string} props.id identifiant de la pièce
 * @param {string} [props.email] adresse du client
 * @param {string} [props.tel] numéro du client, au format international sans « + »
 * @param {string} props.sujet objet du courriel
 * @param {(lien: string) => string} props.message texte, le lien y prenant place
 * @param {() => void} [props.onRelance] appelé quand l'un des deux boutons est touché
 * @param {() => Promise<object>} [props.envoiServeur] envoi par SwissTech, PDF joint ; quand la
 *   boîte de l'entreprise est raccordée, l'e-mail part ainsi, de son adresse
 * @param {() => void} [props.onWhatsApp] ouvre l'envoi de la pièce (PDF joint, salutation,
 *   lien, signature) au lieu d'une ligne de texte dans WhatsApp
 * @returns {JSX.Element} les deux boutons
 */
export function RelanceParLien({
  type,
  id,
  email,
  tel,
  sujet,
  message,
  onRelance,
  envoiServeur,
  onWhatsApp,
}) {
  const { lien, pret } = useLienClient(type, id);
  const boite = useBoiteEntreprise();
  const [envoi, setEnvoi] = useState({ etat: '', message: '' });
  if (!pret) {
    return (
      <div style={{ fontSize: 8.5, color: C.muted, marginTop: 8 }} role="status">
        Préparation du lien…
      </div>
    );
  }
  // Sans lien — pièce pas encore enregistrée, serveur injoignable — le message
  // part tout de même : il nomme la pièce et son montant.
  const texte = message(lien?.url || '').trim();
  const parSwissTech = !!boite && typeof envoiServeur === 'function';
  const envoieParSwissTech = async () => {
    setEnvoi({ etat: 'envoi', message: '' });
    try {
      const r = await envoiServeur();
      setEnvoi({ etat: 'ok', message: 'Envoyé à ' + (r?.destinataire || email || '') });
      if (onRelance) onRelance();
    } catch (e) {
      setEnvoi({ etat: 'echec', message: e?.message || 'L’e-mail n’est pas parti.' });
    }
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginTop: 8 }}>
      {parSwissTech ? (
        <div
          role="button"
          onClick={envoi.etat === 'envoi' ? undefined : envoieParSwissTech}
          style={{ cursor: 'pointer' }}
        >
          <div style={bouton(C.navy)}>
            {envoi.etat === 'envoi' ? 'Envoi…' : '✉ Relancer par email'}
          </div>
        </div>
      ) : (
        <a
          href={
            'mailto:' +
            (email || '') +
            '?subject=' +
            encodeURIComponent(sujet) +
            '&body=' +
            encodeURIComponent(texte)
          }
          onClick={onRelance}
          style={{ textDecoration: 'none' }}
        >
          <div style={bouton(C.navy)}>✉ Relancer par email</div>
        </a>
      )}
      {onWhatsApp ? (
        <div role="button" onClick={onWhatsApp} style={{ cursor: 'pointer' }}>
          <div style={bouton(C.green)}>WhatsApp</div>
        </div>
      ) : (
        <a
          href={'https://wa.me/' + (tel || '') + '?text=' + encodeURIComponent(texte)}
          target="_blank"
          rel="noreferrer"
          onClick={onRelance}
          style={{ textDecoration: 'none' }}
        >
          <div style={bouton(C.green)}>WhatsApp</div>
        </a>
      )}
      {envoi.message && (
        <div
          role="status"
          style={{
            gridColumn: '1 / -1',
            fontSize: 9,
            fontWeight: 700,
            color: envoi.etat === 'echec' ? C.red : C.green,
          }}
        >
          {envoi.etat === 'echec' ? '✕ ' : '✓ '}
          {envoi.message}
        </div>
      )}
    </div>
  );
}
